import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Spinner, makeStyles, shorthands, Label } from "@fluentui/react-components";
import LoggingService from "../services/loggingService";
import { MessageType } from "../enums/messageType";
import { useNavigate } from "react-router-dom";
import { IdentityProvider } from "../enums/identityProvider";
import SessionService from "../services/sessionService";
import { CONSTANTS } from "../constants/constants";

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '90vh',
  },
});

export default function ExternalLoginIdp() {
  const auth = useAuth();
  const styles = useStyles();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string>('');
  
  useEffect(() => {
    const run = async () => {
      try {
        const provider = sessionStorage.getItem("IdentityProvider") as IdentityProvider;
        LoggingService.log(LoggingService.isLoggingEnabled() ? `ExternalLoginIdp - Provider: ${provider}` : '', MessageType.Info);

        if (provider === IdentityProvider.PI) {

          // Fallback: redirect to Platform Identity discover if refresh failed or not available
          const email = sessionStorage.getItem("UserName") || '';
          let orgReference = sessionStorage.getItem("UserOrgReference") || '';
          
          if (email && orgReference) {
            LoggingService.log(LoggingService.isLoggingEnabled() ? `ExternalLoginIdp - Calling redirectToPlatformIdentity for ${email}` : '', MessageType.Info);
            const authResult = await auth.redirectToPlatformIdentity(email, orgReference);
            LoggingService.log(LoggingService.isLoggingEnabled() ? `ExternalLoginIdp - redirectToPlatformIdentity result: ${JSON.stringify(authResult)}` : '', MessageType.Info);
            console.log('[ExternalLoginIdp] Authentication result:', authResult);
            
            if (authResult.success) {
              LoggingService.log(LoggingService.isLoggingEnabled() ? `ExternalLoginIdp - Authentication successful, navigating to tab` : '', MessageType.Info);
              console.log('[ExternalLoginIdp] Authentication successful, navigating to tab');

              const lastLoginEmail = sessionStorage.getItem(CONSTANTS.userName);
              if (lastLoginEmail) {
                localStorage.setItem(CONSTANTS.lastLoginEmail, lastLoginEmail);
              }
              navigate("/tab");
            } else {
              console.log('[ExternalLoginIdp] Authentication failed with error type:', authResult.errorType);
              LoggingService.log(LoggingService.isLoggingEnabled() ? 
                `ExternalLoginIdp - Authentication failed: ${authResult.errorType} - ${authResult.errorMessage}` : '', 
                MessageType.Error);
              
              // Determine the error message
              let errorMsg = '';
              switch (authResult.errorType) {
                case 'userNotFound':
                  errorMsg = authResult.errorMessage || CONSTANTS.piUserNotFoundMessage;
                  break;
                case 'schemaRefreshRunning':
                  errorMsg = authResult.errorMessage || CONSTANTS.schemaRefreshRunningMessage;
                  break;
                case 'authFailed':
                  errorMsg = authResult.errorMessage || CONSTANTS.authenticationFailedMessage;
                  break;
                default:
                  //errorMsg = authResult.errorMessage || CONSTANTS.signInErrorMessage;
                  break;
              }
              
              setErrorMessage(errorMsg);
              
              // Navigate back to login with error message after 3 seconds
              setTimeout(() => {
                navigate("/loginRedirect", { state: { errorMessage: errorMsg } });
              }, 3000);
            }
          } else {
            // If we cannot determine org, go to loginRedirect to collect email/region
            LoggingService.log(LoggingService.isLoggingEnabled() ? `ExternalLoginIdp - Missing email or orgReference, redirecting to login` : '', MessageType.Info);
            navigate("/loginRedirect");
          }
        } else {
          // IDS flow - align behaviour with PI
          LoggingService.log(LoggingService.isLoggingEnabled() ? `ExternalLoginIdp - IDS provider detected, redirecting to IdP` : '', MessageType.Info);
          const idsAuthSuccess = await auth.redirectUserToIdp();

          if (idsAuthSuccess) {
            const lastLoginEmail = sessionStorage.getItem(CONSTANTS.userName);
            if (lastLoginEmail) {
              localStorage.setItem(CONSTANTS.lastLoginEmail, lastLoginEmail);
            }

            LoggingService.log(LoggingService.isLoggingEnabled() ? `ExternalLoginIdp - IDS authentication successful` : '', MessageType.Info);
            navigate("/tab");
          } else {
            LoggingService.log(LoggingService.isLoggingEnabled() ? `ExternalLoginIdp - IDS authentication failed` : '', MessageType.Error);
            const errorMsg = CONSTANTS.signInErrorMessage;
            setErrorMessage(errorMsg);
            setTimeout(() => {
              navigate("/loginRedirect", { state: { errorMessage: errorMsg } });
            }, 3000);
          }
        }
      } catch (err) {
        LoggingService.log(LoggingService.isLoggingEnabled() ? `ExternalLoginIdp - error - ${err}` : '', MessageType.Error);
        const errorMsg = CONSTANTS.signInErrorMessage;
        setErrorMessage(errorMsg);
        setTimeout(() => {
          navigate("/loginRedirect", { state: { errorMessage: errorMsg } });
        }, 3000);
      }
    };

    run();
  }, []);

  return (
    <div className={styles.container}> 
      {errorMessage ? (
        <Spinner size="large" labelPosition="below" label="Redirecting to login page..." />
      ) : (
        <Spinner size="large" labelPosition="below" label="Processing sign-in..." />
      )}
    </div>
  );
}

